import React, { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SparkProjectDetails, SparkFlow } from '../types/sparkTypes';
import styles from '../styles/ProjectBriefingPanel.module.css';
import ReactMarkdown from 'react-markdown';
import { useCitation } from '../../../hooks/useCitation';
import { BriefingIcon } from './ProjectSidebar';
import { normalizeMarkdownBullets } from '../utils/markdown';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UploadIcon from '@mui/icons-material/Upload';

interface ProjectBriefingPanelProps {
    project: SparkProjectDetails;
    flow: SparkFlow | null;
    onOpenAssistant?: () => void;
    onUpload?: () => void;
}

export const ProjectBriefingPanel: React.FC<ProjectBriefingPanelProps> = ({
    project,
    flow,
    onOpenAssistant,
    onUpload,
}) => {
    const hasBriefing = !!(project.briefing && project.briefing.trim());
    const isClosed = !!project.is_closed;

    // Navigation for citation drill-down
    const navigate = useNavigate();
    const location = useLocation();
    const onNavigateToArticle = useCallback((fqn: string, initialChapter?: string) => {
        navigate(`/viewer/${encodeURIComponent(fqn)}`, {
            state: { initialChapter, returnTo: location.pathname + location.search, returnState: { initialStepIndex: 1 } }
        });
    }, [navigate, location]);

    // Use citation hook for markdown rendering with inline citation support
    const {
        markdownComponents,
        markdownPlugins,
        CitationPortal,
    } = useCitation({ projectFqn: project.fqn, onNavigateToArticle });

    return (
        <div className={styles.briefingPanelContainer}>
            {/* Briefing Content */}
            <div className={styles.briefingContent}>
                {hasBriefing ? (
                    <div className={styles.markdownViewer}>
                        <ReactMarkdown
                            components={markdownComponents}
                            remarkPlugins={markdownPlugins}
                        >
                            {normalizeMarkdownBullets(project.briefing)}
                        </ReactMarkdown>
                    </div>
                ) : (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyStateIcon}>
                            <BriefingIcon />
                        </div>
                        <h3 className={styles.emptyStateTitle}>No briefing yet</h3>
                        <p className={styles.emptyStateDescription}>
                            Generate a briefing with AI to define your project scope, or upload an
                            existing briefing document to get started.
                        </p>
                        <div className={styles.emptyStateActions}>
                            <button
                                type="button"
                                className={styles.generateButton}
                                onClick={onOpenAssistant}
                                disabled={isClosed}
                            >
                                <AutoAwesomeIcon fontSize="small" />
                                Edit briefing
                            </button>
                            <button
                                type="button"
                                className={styles.uploadButton}
                                onClick={onUpload}
                                disabled={isClosed}
                            >
                                <UploadIcon fontSize="small" />
                                Upload briefing
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Citation Portal for lightboxes and tooltips */}
            <CitationPortal />
        </div>
    );
};
